import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Autosuggest from 'react-autosuggest';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import AIRPORTS from './airports-parsed.json';
import GoogleComponents from './Google';

// Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faPlane, faCity } from '@fortawesome/pro-light-svg-icons';

// Door (postcode) locations are the same for every AddressInput in a search (source + destination),
// so cache them at module level for the session and de-dupe concurrent fetches with a shared promise.
// The first fetch is slow (it derives the door list from all the org's land rates), so we prefetch
// it as soon as a land leg is being routed — by the time the user types, it's warm.
const doorLocationsCache = { orgId: null, doors: null, promise: null };
function fetchDoorLocations({ organizationId, accesstoken }) {
    if (doorLocationsCache.orgId === organizationId && doorLocationsCache.doors) return Promise.resolve(doorLocationsCache.doors);
    if (doorLocationsCache.orgId === organizationId && doorLocationsCache.promise) return doorLocationsCache.promise;
    doorLocationsCache.orgId = organizationId;
    doorLocationsCache.doors = null;
    doorLocationsCache.promise = axios.get(`/api/rates/door-locations?organizationId=${organizationId}&accesstoken=${accesstoken}`)
        .then(({ data }) => { const doors = Array.isArray(data) ? data : []; doorLocationsCache.doors = doors; doorLocationsCache.promise = null; return doors; })
        .catch(() => { doorLocationsCache.doors = []; doorLocationsCache.promise = null; return []; });
    return doorLocationsCache.promise;
}

function AddressInput({
    placeholder,
    onChange,
    error = null,
    classes = {},
    textFieldProps = {},
    disabled,
    value,
    transportationMethod,
    seaPorts,
    unlocode,
    nogmaps,
    organization,
    accesstoken,
    // When set (e.g. the opportunity route picker), offer door locations regardless of the leg's
    // transportationMethod — so a door-to-door shipment's route can be captured for the quote even
    // on a sea/air opp. Rate search leaves this off, so doors there stay land-only.
    enableDoorLocations = false,
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [cargofivePortSuggestions, setCargofivePortSuggestions] = useState([]);
    const [cargofiveUnlocodeSuggestions, setCargofiveUnlocodeSuggestions] = useState([]);
    const [portSuggestions, setPortSuggestions] = useState([]);
    const [airportSuggestions, setAirportSuggestions] = useState([]);
    const [unlocodeSuggestions, setUnlocodeSuggestions] = useState([]);
    const [doorSuggestions, setDoorSuggestions] = useState([]);
    const [addressStr, setAddressStr] = useState(value);

    // Prefetch door locations as soon as a land leg is being routed with doorRates on, so the first
    // (slow) fetch is warm by the time the user types a suburb/postcode.
    useEffect(() => {
        if ((transportationMethod === 'land' || enableDoorLocations) && _.get(organization, 'integrations.doorRates.enabled', false) && organization?.id && accesstoken) {
            fetchDoorLocations({ organizationId: organization.id, accesstoken });
        }
    }, [transportationMethod, enableDoorLocations, organization, accesstoken]);
    const lastValueRef = useRef(null);
    const propsRef = useRef({ transportationMethod, seaPorts, unlocode, nogmaps, organization, accesstoken, enableDoorLocations });

    // Keep props ref up to date
    useEffect(() => {
        propsRef.current = { transportationMethod, seaPorts, unlocode, nogmaps, organization, accesstoken, enableDoorLocations };
    }, [transportationMethod, seaPorts, unlocode, nogmaps, organization, accesstoken]);

    // Check if CargoFive is enabled for rates
    const cargofiveEnabled = useMemo(() => {
        return _.get(organization, 'integrations.cargofive.enabled', false) && 
               _.get(organization, 'integrations.cargofive.useForRates', false);
    }, [organization]);

    // Create debounced CargoFive search function (0.5s delay)
    const debouncedCargofiveSearch = useMemo(
        () => _.debounce(async (inputValue, organization, accesstoken) => {
            // Check again if input has changed
            if (inputValue !== lastValueRef.current) return;
            
            try {
                // Build base query params manually since accesstoken is already URL-encoded
                const baseParams = [
                    `search=${encodeURIComponent(inputValue)}`,
                    `accesstoken=${accesstoken}`,
                    `organizationId=${organization.id}`,
                ];
                
                // Perform two searches: one for ports (place_type_id=1) and one for unlocodes (place_type_id=2)
                const [portsResponse, unlocodesResponse] = await Promise.all([
                    axios.get(`/api/cargofive/places?${baseParams.join('&')}&place_type_id=1`),
                    axios.get(`/api/cargofive/places?${baseParams.join('&')}&place_type_id=2`)
                ]);
                
                if (inputValue !== lastValueRef.current) return;
                
                // Map cargofive API response to fit PortSchema for Monada structure
                const portItems = Array.isArray(portsResponse?.data?.data) ? portsResponse.data.data : (Array.isArray(portsResponse?.data) ? portsResponse.data : []);
                const unlocodeItems = Array.isArray(unlocodesResponse?.data?.data) ? unlocodesResponse.data.data : (Array.isArray(unlocodesResponse?.data) ? unlocodesResponse.data : []);

                // Helper to map Cargofive object to Monada PortSchema
                function mapCargofivePlace(place) {
                    // type: map place_type_id to a string
                    // Monada wants: 'port', 'unlocode', 'city', 'country', 'geocode'
                    let type = 'port';
                    if (place.place_type_id === 2) {
                        type = 'unlocode';
                    } else if (place.place_type_id === 3) {
                        type = 'city';
                    } // adapt if more mappings needed
                    return {
                        type,
                        id: String(place.code),
                        text: place.display_name || place.name || place.code,
                        countryCode: (place.api_variations?.one?.countryCode || place.api_variations?.oocl?.ctryCode || (place.code || '').substring(0, 2) || ''),
                        city: place.name || '',
                        externalIds: {
                            ...(place.id && { cargofiveId: String(place.id) }),
                            ...(place.uuid && { cargofiveUuid: String(place.uuid) }),
                            ...(place.code && { code: String(place.code) })
                        }
                    };
                }

                const ports = Array.isArray(portItems)
                    ? portItems.map(mapCargofivePlace)
                    : [];
                const unlocodes = Array.isArray(unlocodeItems)
                    ? unlocodeItems.map(mapCargofivePlace)
                    : [];
                
                setCargofivePortSuggestions(ports);
                setCargofiveUnlocodeSuggestions(unlocodes);
                setPortSuggestions([]);
                setAirportSuggestions([]);
                setUnlocodeSuggestions([]);
                setSuggestions([]);
            } catch (e) {
                console.error('[CargoFive] Error searching places:', e);
                console.error('[CargoFive] Error details:', e?.response?.data || e.message);
                // Fall through to empty results
                setCargofivePortSuggestions([]);
                setCargofiveUnlocodeSuggestions([]);
                setPortSuggestions([]);
                setAirportSuggestions([]);
                setUnlocodeSuggestions([]);
                setSuggestions([]);
            }
        }, 500),
        [] // Empty deps - debounced function created once
    );

    // Sync value prop to addressStr state
    useEffect(() => {
        setAddressStr(value);
    }, [value]);

    const handleSuggestionsFetchRequested = useMemo(
        () => _.debounce(async ({ value: inputValue }) => {
            lastValueRef.current = inputValue;
            const { transportationMethod, seaPorts, unlocode, nogmaps, organization, accesstoken, enableDoorLocations } = propsRef.current;
            
            // If CargoFive is enabled for rates AND transportationMethod is 'sea', use CargoFive search
            const cargofiveEnabled = _.get(organization, 'integrations.cargofive.enabled', false) && 
                                     _.get(organization, 'integrations.cargofive.useForRates', false);
            
            if (cargofiveEnabled && transportationMethod === 'sea' && organization && accesstoken && inputValue.length >= 3) {
                // Trigger the debounced CargoFive search (waits 0.5s from last call)
                debouncedCargofiveSearch(inputValue, organization, accesstoken);
                return;
            }
            
            // Original search logic when CargoFive is not enabled
            const types = ['geocode', 'establishment'];

            const unsortedPorts = inputValue.length > 1 && (transportationMethod === 'sea' || transportationMethod === 'land') 
                ? _.filter(seaPorts, f => {
                    const text = `${f.code} - ${f.portname}`;
                    return text.toUpperCase().indexOf(inputValue.toUpperCase()) > -1;
                }) 
                : [];
            
            const ports = _.sortBy(unsortedPorts, f => {
                if ((f.code || '').toUpperCase().startsWith(inputValue.toUpperCase())) return 0;
                if ((f.portname || '').toUpperCase().startsWith(inputValue.toUpperCase())) return 1;
                return 2;
            });

            const airports = inputValue.length > 1 && (transportationMethod === 'air' || transportationMethod === 'land') 
                ? _.compact([
                    _.find(AIRPORTS, f => f.iata_code === inputValue), 
                    ..._.filter(AIRPORTS, f => {
                        if (!f.iata_code) return false;
                        if (f.iata_code === inputValue) return false;
                        return f.iata_code.toUpperCase().indexOf(inputValue.toUpperCase()) > -1;
                    }),
                    ..._.filter(AIRPORTS, f => {
                        if (!f.iata_code) return false;
                        if (f.iata_code === inputValue) return false;
                        if (f.iata_code.toUpperCase().indexOf(inputValue.toUpperCase()) > -1) return false;
                        return f.name?.toUpperCase().indexOf(inputValue.toUpperCase()) > -1;
                    })
                ]) 
                : [];

            const unsortedUnlocode = inputValue.length > 1 
                ? (unlocode || []).filter(f => {
                    if (f.code.toUpperCase().indexOf(inputValue.toUpperCase()) > -1) return true;
                    if (f.text.toUpperCase().indexOf(inputValue.toUpperCase()) > -1) return true;
                    return false;
                }) 
                : [];

            const _unlocode = _.sortBy(unsortedUnlocode, f => {
                if ((f.code || '').toUpperCase().startsWith(inputValue.toUpperCase())) return 0;
                if ((f.text || '').toUpperCase().startsWith(inputValue.toUpperCase())) return 1;
                return 2;
            });

            setPortSuggestions(ports);
            setAirportSuggestions(airports);
            setUnlocodeSuggestions(_unlocode);

            // Door (postcode) locations: when the doorRates integration is on and we're routing a
            // land leg, suggest the org's ingested door locations by suburb/postcode. Fetched once
            // per org (deterministic ids) and filtered here so the picked door matches a real rate.
            const doorRatesOn = _.get(organization, 'integrations.doorRates.enabled', false);
            if (doorRatesOn && (transportationMethod === 'land' || enableDoorLocations) && organization?.id && accesstoken && inputValue.length >= 2) {
                const doors = await fetchDoorLocations({ organizationId: organization.id, accesstoken });
                if (lastValueRef.current !== inputValue) return; // a newer keystroke superseded this one
                const needle = inputValue.toLowerCase();
                setDoorSuggestions(doors.filter(d => `${d.city || ''} ${d.text || ''} ${d.id || ''}`.toLowerCase().includes(needle)).slice(0, 50));
            } else {
                setDoorSuggestions([]);
            }

            if (ports.length + airports.length + _unlocode.length >= 50) {
                setSuggestions([]);
                return;
            }

            // Perform the actual request
            const request = { types, /* componentRestrictions: {country: countryCode}, */ input: inputValue };
            const googleSuggestions = !GoogleComponents.autocomplete || nogmaps 
                ? [] 
                : await new Promise((res) => GoogleComponents.autocomplete.getPlacePredictions(request, v => res(v)));

            if (!googleSuggestions) return;

            if (inputValue !== lastValueRef.current) return;

            setSuggestions(googleSuggestions);
        }, 200),
        [] // Empty deps - debounced function created once
    );

    const handleSuggestionsClearRequested = useCallback(() => {
        setSuggestions([]);
        setCargofivePortSuggestions([]);
        setCargofiveUnlocodeSuggestions([]);
        setPortSuggestions([]);
        setAirportSuggestions([]);
        setUnlocodeSuggestions([]);
    }, []);

    const getGmapsSuggestionValue = useCallback((suggestion) => {
        const request = {
            placeId: suggestion.place_id,
            fields: ['name', 'address_components', 'types']
        };

        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails(request, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                const type = (() => {
                    if (place.types.includes('country')) return 'country';
                    if (place.types.includes('locality')) return 'city';
                    return 'geocode';
                })();
                const countryCode = place.address_components.find(ac => ac.types.includes('country')).short_name;
                const city = (place.address_components.find(ac => ac.types.includes('locality')) || {}).short_name;
                onChange({ text: suggestion.description, countryCode, city, type, id: suggestion.place_id });
            }
        });
        return suggestion.description;
    }, [onChange]);

    const getPortsSuggestionValue = useCallback((suggestion) => {
        const text = `${suggestion.code} - ${suggestion.portname}`;
        onChange({ text, countryCode: suggestion.code.substr(0,2), type: 'port', id: suggestion.code });
        return text;
    }, [onChange]);

    const getAirportsSuggestionValue = useCallback((suggestion) => {
        const text = `${suggestion.iata_code} - ${suggestion.name}`;
        onChange({ text, countryCode: suggestion.iso_country, type: 'port', id: suggestion.iata_code });
        return text;
    }, [onChange]);

    const getUnlocodeSuggestionValue = useCallback((suggestion) => {
        const text = `${suggestion.code} - ${suggestion.text}`;
        onChange({ text, countryCode: suggestion.code.substr(0,2), type: 'unlocode', id: suggestion.code });
        return text;
    }, [onChange]);

    const getCargofivePortSuggestionValue = useCallback((suggestion) => {
        // CargoFive suggestions are already mapped to Monada PortSchema structure
        onChange({ 
            text: suggestion.text, 
            countryCode: suggestion.countryCode, 
            type: suggestion.type || 'port', 
            id: suggestion.id,
            city: suggestion.city,
            externalIds: suggestion.externalIds,
            cargofivePlaceId: suggestion.cargofivePlaceId,
            cargofivePlaceUuid: suggestion.cargofivePlaceUuid,
        });
        return suggestion.text;
    }, [onChange]);

    const getCargofiveUnlocodeSuggestionValue = useCallback((suggestion) => {
        // CargoFive suggestions are already mapped to Monada PortSchema structure
        onChange({ 
            text: suggestion.text, 
            countryCode: suggestion.countryCode, 
            type: suggestion.type || 'unlocode', 
            id: suggestion.id,
            city: suggestion.city,
            externalIds: suggestion.externalIds,
            cargofivePlaceId: suggestion.cargofivePlaceId,
            cargofivePlaceUuid: suggestion.cargofivePlaceUuid,
        });
        return suggestion.text;
    }, [onChange]);

    const getDoorSuggestionValue = useCallback((suggestion) => {
        onChange({ text: suggestion.text, countryCode: suggestion.countryCode, type: 'postcode', id: suggestion.id, city: suggestion.city });
        return suggestion.text;
    }, [onChange]);

    const getSuggestionValue = useCallback((suggestion) => {
        if (suggestion.type === 'door') return getDoorSuggestionValue(suggestion.suggestion);
        if (suggestion.type === 'gmap') return getGmapsSuggestionValue(suggestion.suggestion);
        if (suggestion.type === 'port') {
            // Check if it's a cargofive port (has cargofivePlaceId or externalIds.cargofiveId) or regular port
            if (suggestion.suggestion.cargofivePlaceId || suggestion.suggestion.externalIds?.cargofiveId) {
                return getCargofivePortSuggestionValue(suggestion.suggestion);
            }
            return getPortsSuggestionValue(suggestion.suggestion);
        }
        if (suggestion.type === 'airport') return getAirportsSuggestionValue(suggestion.suggestion);
        if (suggestion.type === 'unlocode') {
            // Check if it's a cargofive unlocode (has cargofivePlaceId or externalIds.cargofiveId) or regular unlocode
            if (suggestion.suggestion.cargofivePlaceId || suggestion.suggestion.externalIds?.cargofiveId) {
                return getCargofiveUnlocodeSuggestionValue(suggestion.suggestion);
            }
            return getUnlocodeSuggestionValue(suggestion.suggestion);
        }
    }, [getGmapsSuggestionValue, getPortsSuggestionValue, getAirportsSuggestionValue, getUnlocodeSuggestionValue, getCargofivePortSuggestionValue, getCargofiveUnlocodeSuggestionValue]);

    const suggestionsData = useMemo(() => {
        // If CargoFive is enabled AND transportationMethod is 'sea', only show CargoFive results
        if (cargofiveEnabled && transportationMethod === 'sea') {
            const cps = _.take(cargofivePortSuggestions, 1000);
            const cus = _.take(cargofiveUnlocodeSuggestions, 1000);
            return _.compact([
                cps.length > 0 ? {
                    title: 'From CargoFive Ports',
                    suggestions: cps.map(s => ({ type: 'port', suggestion: s })),
                } : null,
                cus.length > 0 ? {
                    title: 'From CargoFive UN/LOCODE',
                    suggestions: cus.map(s => ({ type: 'unlocode', suggestion: s })),
                } : null
            ]);
        }
        
        // Original logic when CargoFive is not enabled
        const ds = _.take(doorSuggestions, 1000);
        const ps = _.take(portSuggestions, 1000);
        const as = _.take(airportSuggestions, 1000);
        const ls = _.take(unlocodeSuggestions, 1000);
        const gs = _.take(suggestions, Math.max(1000 - ps.length - as.length - ls.length, 0));

        return _.compact([
            ds.length > 0 ? {
                title: 'Door Locations',
                suggestions: ds.map(s => ({ type: 'door', suggestion: s })),
            } : null,
            ps.length > 0 ? {
                title: 'From Sea Ports',
                suggestions: ps.map(s => ({ type: 'port', suggestion: s })),
            } : null,
            as.length > 0 ? {
                title: 'From Airports',
                suggestions: as.map(s => ({ type: 'airport', suggestion: s })),
            } : null,
            ls.length > 0 ? {
                title: 'From UN/LOCODE',
                suggestions: ls.map(s => ({ type: 'unlocode', suggestion: s })),
            } : null,
            gs.length > 0 ? {
                title: 'From Google Maps',
                suggestions: gs.map(s => ({ type: 'gmap', suggestion: s })),
            } : null
        ]);
    }, [cargofiveEnabled, cargofivePortSuggestions, cargofiveUnlocodeSuggestions, doorSuggestions, portSuggestions, airportSuggestions, unlocodeSuggestions, suggestions, transportationMethod]);

    return (
        <div className={classes.root} style={{ width: '100%', position: 'relative' }}>
            <div>
                <Autosuggest
                    focusInputOnSuggestionClick={false}
                    onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                    onSuggestionsClearRequested={handleSuggestionsClearRequested}
                    getSuggestionValue={getSuggestionValue}
                    renderSuggestion={renderSuggestion}
                    renderInputComponent={renderInputComponent}
                    renderSectionTitle={renderSectionTitle}
                    getSectionSuggestions={getSectionSuggestions}
                    multiSection={true}
                    suggestions={suggestionsData}
                    renderSuggestionsContainer={renderSuggestionsContainer}
                    inputProps={{
                        placeholder: placeholder,
                        value: addressStr || '',
                        disabled,
                        onChange: (e, { newValue }) => setAddressStr(newValue),
                        onBlur: () => setAddressStr(value),
                        autoFocus: true,
                        error: !!error,
                        ...textFieldProps,
                    }}
                />
            </div>
        </div>
    );
}

function renderSectionTitle(section) {
    return (
        <div style={{ paddingLeft: '3px' }}>
            <Typography variant='caption'>
                {section.title}
            </Typography>
        </div>
    );
}

function getSectionSuggestions(section) {
    return section?.suggestions || [];
}

function renderSuggestion(suggestion, { query, isHighlighted }) {
    if (suggestion.type === 'gmap') 
        return renderGmapsSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
    if (suggestion.type === 'port') {
        // Check if it's a cargofive port (has text property from Monada structure) or regular port
        if (suggestion.suggestion.text) {
            // CargoFive/Monada structure - render using CargoFive port renderer
            return renderCargofivePortSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
        }
        return renderPortsSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
    }
    if (suggestion.type === 'unlocode') {
        // Check if it's a cargofive unlocode (has text property from Monada structure) or regular unlocode
        if (suggestion.suggestion.text) {
            // CargoFive/Monada structure - render using CargoFive unlocode renderer
            return renderCargofiveUnlocodeSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
        }
        return renderUnlocodeSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
    }
    if (suggestion.type === 'airport')
        return renderAirportsSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
    if (suggestion.type === 'door')
        return renderDoorSuggestion({ suggestion: suggestion.suggestion, query, isHighlighted });
}

function renderDoorSuggestion({ suggestion, query, isHighlighted }) {
    const text = suggestion.text || suggestion.city || suggestion.id;
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);
    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>{part.text}</span>
                ) : (
                    <span key={String(index)}>{part.text}</span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderGmapsSuggestion({suggestion, query, isHighlighted }) {
    const text = suggestion.description;
    console.log('[renderSuggestion]', text);

    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderPortsSuggestion({suggestion, query, isHighlighted }) {
    const text = `${suggestion.code} - ${suggestion.portname}`;
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faShip} style={{ color: '#666', marginRight: '5px' }} />
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderAirportsSuggestion({suggestion, query, isHighlighted }) {
    const text = `${suggestion.iata_code} - ${suggestion.name}`;
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faPlane} style={{ color: '#666', marginRight: '5px' }} />
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderUnlocodeSuggestion({suggestion, query, isHighlighted }) {
    const text = `${suggestion.code} - ${suggestion.text}`;
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faCity} style={{ color: '#666', marginRight: '5px' }} />
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderCargofivePortSuggestion({suggestion, query, isHighlighted }) {
    // CargoFive suggestions are now in Monada PortSchema structure
    const text = suggestion.text || '';
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faShip} style={{ color: '#666', marginRight: '5px' }} />
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function renderCargofiveUnlocodeSuggestion({suggestion, query, isHighlighted }) {
    // CargoFive suggestions are now in Monada PortSchema structure
    const text = suggestion.text || '';
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ textWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faCity} style={{ color: '#666', marginRight: '5px' }} />
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

AddressInput.propTypes = {
    value: PropTypes.string,
    textFieldProps: PropTypes.shape({}),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    placeholder: PropTypes.string,
    classes: PropTypes.shape({}),
    disabled: PropTypes.bool,
    transportationMethod: PropTypes.string,
    seaPorts: PropTypes.array,
    unlocode: PropTypes.array,
    nogmaps: PropTypes.bool,
    organization: PropTypes.shape({}),
    accesstoken: PropTypes.string,
};

AddressInput.defaultProps = {
    textFieldProps: null,
    value: null,
    error: null,
    placeholder: null,
    classes: {},
    disabled: false,
    transportationMethod: null,
    seaPorts: [],
    unlocode: [],
    nogmaps: false,
    organization: null,
    accesstoken: null,
};

function renderInputComponent(inputProps) {
    const {
        classes, inputRef = () => {}, ref, ...other
    } = inputProps;

    return (
        <TextField
            fullWidth
            inputProps={{
                ref: (node) => {
                    ref(node);
                    inputRef(node);
                },
            }}
            InputProps={{
                startAdornment: other.startAdornment
            }}
            {..._.omit(other, ['key', 'startAdornment'])}
            key={other.key}
        />
    );
}

export default AddressInput;

function renderSuggestionsContainer(options) {
    const containerProps = _.omit(options.containerProps, ['key']);
    const key = options.containerProps.key;
    return (
        <Paper {...containerProps} key={key} classes={{ root: 'registration-no-ul' }} sx={{ position: 'absolute !important', width: '100%', maxHeight: '300px', overflowY: 'auto' }} square>
            {options.children}
        </Paper>
    );
}